/**
 * Embla Carousel Initialization
 *
 * Initializes all Embla carousels on the page with support for:
 * - Autoplay with play/pause controls
 * - Automatic height adjustment (LG+ screens only)
 * - Navigation buttons (prev/next)
 * - Dot-based pagination
 * - Special handling for testimonial carousels
 *
 * @requires EmblaCarousel - Main carousel library
 * @requires EmblaCarouselAutoplay - Autoplay plugin
 * @requires EmblaCarouselAutoHeight - Dynamic height plugin
 * @requires EmblaCarouselClassNames - CSS class management plugin
 */

document.addEventListener('DOMContentLoaded', () => {
  // ==========================================================================
  // Constants & Dependencies
  // ==========================================================================

  const EmblaCarousel = window.EmblaCarousel;
  const AutoPlay = window.EmblaCarouselAutoplay;
  const AutoHeight = window.EmblaCarouselAutoHeight;
  const ClassNames = window.EmblaCarouselClassNames;
  const LG_BREAKPOINT = 992; // Bootstrap's LG breakpoint

  // ==========================================================================
  // Helper Functions
  // ==========================================================================

  /**
   * Finds carousel control elements within the carousel and its parent container.
   * Searches both inside the carousel node and its closest parent component/container
   * to support flexible control placement.
   *
   * @param {HTMLElement} emblaNode - The carousel container element
   * @param {string} selector - CSS selector for the control elements
   * @returns {HTMLElement[]} Array of matching control elements (without duplicates)
   */
  function findControls(emblaNode, selector) {
    const controls = Array.from(emblaNode.querySelectorAll(selector));
    const parent = emblaNode.closest('.component, .container');

    if (parent) {
      parent.querySelectorAll(selector).forEach(control => {
        // Avoid duplicates if control is in both carousel and parent
        if (!controls.includes(control)) {
          controls.push(control);
        }
      });
    }

    return controls;
  }

  /**
   * Sets up play/pause functionality for autoplay controls.
   * Manages button state, ARIA attributes, and user interactions.
   *
   * @param {HTMLElement[]} buttons - Array of play/pause button elements
   * @param {Object} autoPlayInstance - Embla autoplay plugin instance
   */
  function setupPlayPause(buttons, autoPlayInstance) {
    // Early return if no buttons or no autoplay instance
    if (buttons.length === 0 || !autoPlayInstance) return;

    let isPlaying = true;

    /**
     * Updates all play/pause buttons with current playback state.
     * Synchronizes ARIA labels and CSS classes across all control buttons.
     */
    function updateButtons() {
      const label = isPlaying ? 'Pause autoplay' : 'Play autoplay';

      buttons.forEach(btn => {
        // Update accessibility attributes
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);

        // Update visual state classes
        btn.classList.toggle('playing', isPlaying);
        btn.classList.toggle('paused', !isPlaying);
      });
    }

    // Attach click handlers to all buttons
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        // Toggle autoplay state
        isPlaying ? autoPlayInstance.stop() : autoPlayInstance.play();
        isPlaying = !isPlaying;
        updateButtons();
      });
    });

    // Initialize button states
    updateButtons();
  }

  /**
   * Sets up previous/next navigation buttons for manual carousel control.
   *
   * @param {HTMLElement} emblaNode - The carousel container element
   * @param {Object} emblaApi - Embla carousel API instance
   */
  function setupNavigation(emblaNode, emblaApi) {
    // Previous button handlers
    findControls(emblaNode, '.embla__prev').forEach(btn => {
      btn.addEventListener('click', () => emblaApi.scrollPrev());
    });

    // Next button handlers
    findControls(emblaNode, '.embla__next').forEach(btn => {
      btn.addEventListener('click', () => emblaApi.scrollNext());
    });
  }

  /**
   * Sets up dot-based pagination controls with progress indicators.
   * Creates interactive dots for each slide and manages their active state.
   *
   * @param {HTMLElement} emblaNode - The carousel container element
   * @param {Object} emblaApi - Embla carousel API instance
   */
  function setupDots(emblaNode, emblaApi) {
    const dotsContainers = findControls(emblaNode, '.embla__dots');
    if (dotsContainers.length === 0) return;

    const allDots = [];
    const delay = Number(emblaNode.dataset.delay) || 4000;

    /**
     * Renders dot elements for each slide in the carousel.
     * Creates accessible progress bars with ARIA attributes.
     */
    function renderDots() {
      dotsContainers.forEach(container => {
        // Generate HTML for each slide's dot indicator
        container.innerHTML = emblaApi.scrollSnapList()
          .map((_, i) => `
            <button type="button"
                    class="embla__dot progress p-0 border-0"
                    aria-label="Go to slide ${i + 1}">
              <span role="progressbar"
                class="progress"
                aria-valuenow="0"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-labelledby="ContentHubHeading"
                data-delay="${delay}"></span>
              <div class="progress-bar bg-primary"></div>
            </button>
          `.trim())
          .join('');

        // Attach click handlers and track all dots
        const dots = Array.from(container.querySelectorAll('.embla__dot'));
        dots.forEach((dot, i) => {
          dot.addEventListener('click', () => emblaApi.scrollTo(i));
          allDots.push(dot);
        });
      });
    }

    /**
     * Updates dot indicators to reflect the current selected slide.
     * Handles multiple dot containers and circular pagination.
     */
    function updateDots() {
      const selected = emblaApi.selectedScrollSnap();
      const snapCount = emblaApi.scrollSnapList().length;

      allDots.forEach((dot, i) => {
        // Handle multiple dot containers by using modulo
        const dotIndex = i % snapCount;
        dot.classList.toggle('embla__dot--selected', dotIndex === selected);
      });
    }

    // Register event handlers for dot updates
    emblaApi.on('init', renderDots);
    emblaApi.on('reInit', renderDots);
    emblaApi.on('init', updateDots);
    emblaApi.on('reInit', updateDots);
    emblaApi.on('select', updateDots);
  }

  /**
   * Updates focusable elements in slides so only in-view slides are keyboard-focusable.
   *
   * @param {Object} emblaApi - Embla carousel API instance
   */
  function setupSlideTabIndex(emblaApi) {
    function updateSlideTabIndex() {
      const slides = emblaApi.slideNodes();
      const snappedSlide = slides.find(slide => slide.classList.contains('is-snapped'));
      const selectedIndex = emblaApi.selectedScrollSnap();

      slides.forEach((slide, index) => {
        const focusableElements = new Set(slide.querySelectorAll('a, button'));

        if (slide.matches('a, button')) {
          focusableElements.add(slide);
        }

        const isActiveSlide = snappedSlide ? slide === snappedSlide : index === selectedIndex;
        const tabIndex = isActiveSlide ? '0' : '-1';

        focusableElements.forEach(element => {
          element.setAttribute('tabindex', tabIndex);
        });
      });
    }

    emblaApi.on('init', updateSlideTabIndex);
    emblaApi.on('reInit', updateSlideTabIndex);
    emblaApi.on('select', updateSlideTabIndex);
    emblaApi.on('slidesInView', updateSlideTabIndex);
    emblaApi.on('resize', updateSlideTabIndex);

    const viewport = window.visualViewport;
    const resizeTarget = viewport || window;
    const onViewportResize = () => updateSlideTabIndex();

    resizeTarget.addEventListener('resize', onViewportResize);

    emblaApi.on('destroy', () => {
      resizeTarget.removeEventListener('resize', onViewportResize);
    });

    updateSlideTabIndex();
  }

  // ==========================================================================
  // Carousel Initialization
  // ==========================================================================

  // Initialize each carousel instance found on the page
  document.querySelectorAll('.embla-carousel').forEach(emblaNode => {
    const emblaContainer = emblaNode.querySelector('.embla__container');
    const isTestimonialList = emblaContainer?.classList.contains('component-testimonial-list');
    const mediaQuery = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);

    // ------------------------------------------------------------------------
    // Configure Carousel Options
    // ------------------------------------------------------------------------

    function getOptions(isLargeScreen) {
      return {
        loop: !isTestimonialList,                              // Disable loop for testimonials
        slidesToScroll: 1,                                     // Scroll one slide at a time
        active: isTestimonialList ? !isLargeScreen : true,     // Testimonials active on mobile only
        align: 'start',                                        // Align slides to start
        containScroll: 'trimSnaps',                            // Remove empty space at end
      };
    }

    // ------------------------------------------------------------------------
    // Configure Plugins
    // ------------------------------------------------------------------------

    const autoHeightEnabled = emblaNode.dataset.autoheight === 'true';
    const classNamesPlugin = ClassNames();
    const autoPlayInstance = AutoPlay({
      playOnInit: emblaNode.dataset.autoplay,               // Start playing automatically
      delay: Number(emblaNode.dataset.delay) || 4000        // Default 4 second delay
    });
    const autoHeightPlugin = autoHeightEnabled ? AutoHeight() : null;

    function getPlugins(isLargeScreen) {
      const plugins = [classNamesPlugin, autoPlayInstance];
      // Only enable auto-height if explicitly enabled via data attribute (and on large screens to prevent layout shifts on mobile)
      if (autoHeightPlugin && isLargeScreen) {
        plugins.splice(1, 0, autoHeightPlugin);
      }
      return plugins;
    }

    // ------------------------------------------------------------------------
    // Initialize Carousel with Options and Plugins
    // ------------------------------------------------------------------------

    const emblaApi = EmblaCarousel(emblaNode, getOptions(mediaQuery.matches), getPlugins(mediaQuery.matches));

    // ------------------------------------------------------------------------
    // Setup UI Controls
    // ------------------------------------------------------------------------

    setupPlayPause(findControls(emblaNode, '.embla__playpause'), autoPlayInstance);
    setupNavigation(emblaNode, emblaApi);
    setupDots(emblaNode, emblaApi);
    setupSlideTabIndex(emblaApi);

    // Reinitialize when crossing LG breakpoint to toggle testimonial activation
    const onBreakpointChange = (event) => {
      const isLargeScreen = event.matches;
      emblaApi.reInit(getOptions(isLargeScreen), getPlugins(isLargeScreen));
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onBreakpointChange);
    }
  });
});
